/** Shared bilinear sampler for geometric-distort filters.
 *
 *  Lens Correction, Twirl, and Pinch all sample the source at
 *  sub-pixel `(sx, sy)` coordinates that don't fall on integer pixel
 *  centers. Naive nearest-neighbor sampling produces visible aliasing
 *  ("blockiness") in the output; bilinear interpolates the four
 *  surrounding pixels by their fractional position so the warped
 *  result reads as smooth.
 *
 *  Edge handling clamps the four sample positions to the image
 *  rectangle — same convention as the blur family. Wrap or zero-pad
 *  would introduce seams or darkening at the perimeter that don't
 *  match user intent for warp filters.
 *
 *  The function writes the interpolated RGBA into `dst[di..di+3]`
 *  rather than returning four numbers, so the inner loop avoids a
 *  per-pixel allocation. */

export function bilinearSample(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  sx: number,
  sy: number,
  dst: Uint8ClampedArray,
  di: number,
): void {
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const fx = sx - x0;
  const fy = sy - y0;
  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const maxX = w - 1;
  const maxY = h - 1;
  // Clamp each corner — the four `Math.min/max` calls keep the
  // sampler safe near image edges without an explicit "in-bounds"
  // branch (those mispredict in hot loops).
  const cx0 = x0 < 0 ? 0 : x0 > maxX ? maxX : x0;
  const cy0 = y0 < 0 ? 0 : y0 > maxY ? maxY : y0;
  const cx1 = x1 < 0 ? 0 : x1 > maxX ? maxX : x1;
  const cy1 = y1 < 0 ? 0 : y1 > maxY ? maxY : y1;
  const i00 = (cy0 * w + cx0) * 4;
  const i10 = (cy0 * w + cx1) * 4;
  const i01 = (cy1 * w + cx0) * 4;
  const i11 = (cy1 * w + cx1) * 4;
  const w00 = (1 - fx) * (1 - fy);
  const w10 = fx * (1 - fy);
  const w01 = (1 - fx) * fy;
  const w11 = fx * fy;
  dst[di] = src[i00] * w00 + src[i10] * w10 + src[i01] * w01 + src[i11] * w11;
  dst[di + 1] = src[i00 + 1] * w00 + src[i10 + 1] * w10 + src[i01 + 1] * w01 + src[i11 + 1] * w11;
  dst[di + 2] = src[i00 + 2] * w00 + src[i10 + 2] * w10 + src[i01 + 2] * w01 + src[i11 + 2] * w11;
  dst[di + 3] = src[i00 + 3] * w00 + src[i10 + 3] * w10 + src[i01 + 3] * w01 + src[i11 + 3] * w11;
}
